/**
 * Canonical, dependency-free support-ticket attachment boundary.
 *
 * Ticket comments have historically stored a mixture of `fileId`, `id`, URL,
 * `downloadUrl`, and Storage-path-only records. Components must never render a
 * Storage path as a URL or silently omit a legacy record. This module turns
 * those historical shapes into the single client-facing ticket attachment DTO.
 */
export type TicketAttachmentStatus = 'available' | 'processing' | 'blocked' | 'missing';

export interface TicketAttachment {
  id: string;
  /** Compatibility alias used by existing ticket card components. */
  fileId: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  /** Authenticated same-origin proxy URL, never a raw storage path. */
  url: string;
  storagePath?: string;
  scanStatus?: string;
  status: TicketAttachmentStatus;
}

export type TicketAttachmentAccessError =
  | 'processing'
  | 'blocked'
  | 'missing';

/**
 * Adds the verified ticket context required by the authenticated upload proxy.
 * Existing ticketId values are replaced rather than duplicated so legacy URLs
 * cannot turn req.query.ticketId into an array and fail authorization.
 */
export function withTicketAttachmentContext(url: string, ticketId?: string): string {
  if (!ticketId || !url.startsWith('/api/uploads/')) return url;

  const hashIndex = url.indexOf('#');
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : '';
  const withoutHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
  const queryIndex = withoutHash.indexOf('?');
  const path = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
  const query = queryIndex >= 0 ? withoutHash.slice(queryIndex + 1) : '';
  const params = new URLSearchParams(query);
  params.set('ticketId', ticketId);
  return `${path}?${params.toString()}${hash}`;
}

const proxyFileId = (url: string): string => {
  const match = url.match(/(?:^|\/)api\/uploads\/([^?#/]+)/i);
  if (!match) return '';
  try { return decodeURIComponent(match[1]); } catch { return match[1]; }
};

const basename = (value: string): string => value.split('/').filter(Boolean).pop() || '';

function normalizeStatus(value: unknown): TicketAttachmentStatus {
  switch (String(value || '').trim().toLowerCase()) {
    case 'pending':
    case 'scanning':
    case 'processing':
      return 'processing';
    case 'infected':
    case 'quarantined':
      return 'blocked';
    case 'failed':
    case 'expired':
    case 'missing':
      return 'missing';
    // Unstamped records pre-date scan metadata. Keep them readable for
    // historical compatibility; server-side authorization remains authoritative.
    default:
      return 'available';
  }
}

/**
 * Normalizes an attachment read from a ticket or ticket comment.
 * Returns null only when there is no stable identifier or safely servable URL.
 */
export function normalizeTicketAttachment(value: unknown): TicketAttachment | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const storagePath = typeof raw.storagePath === 'string' ? raw.storagePath.trim().replace(/^\/+/, '') : '';
  const rawUrl = [raw.url, raw.downloadUrl, raw.fileUrl, raw.attachmentUrl]
    .find((candidate): candidate is string => typeof candidate === 'string' && candidate.trim().length > 0)?.trim() || '';
  const id = [raw.fileId, raw.id, proxyFileId(rawUrl), storagePath ? basename(storagePath) : '']
    .find((candidate): candidate is string => typeof candidate === 'string' && candidate.trim().length > 0)?.trim() || '';
  if (!id) return null;

  // Only relative proxy URLs, absolute URLs and local object/data URLs are
  // displayable. A raw gs:// or ticket_attachments/... path is converted to the
  // authenticated proxy by id; it is never passed to img/a/iframe directly.
  const url = /^(?:\/|https?:\/\/|blob:|data:)/i.test(rawUrl)
    ? rawUrl
    : `/api/uploads/${encodeURIComponent(id)}`;
  const fileName = [raw.fileName, raw.name, raw.originalName, raw.filename]
    .find((candidate): candidate is string => typeof candidate === 'string' && candidate.trim().length > 0)?.trim()
    || (storagePath ? basename(storagePath) : 'Attachment');
  const size = Number(raw.fileSize ?? raw.size ?? raw.sizeBytes ?? 0);
  const scanStatus = typeof raw.scanStatus === 'string' ? raw.scanStatus : undefined;

  return {
    id,
    fileId: id,
    fileName,
    fileSize: Number.isFinite(size) && size > 0 ? size : 0,
    mimeType: typeof raw.mimeType === 'string' && raw.mimeType ? raw.mimeType : (typeof raw.contentType === 'string' && raw.contentType ? raw.contentType : 'application/octet-stream'),
    url,
    ...(storagePath ? { storagePath } : {}),
    ...(scanStatus ? { scanStatus } : {}),
    status: normalizeStatus(scanStatus),
  };
}

export function ticketAttachmentAccessError(attachment: TicketAttachment): TicketAttachmentAccessError | null {
  if (attachment.status === 'processing') return 'processing';
  if (attachment.status === 'blocked') return 'blocked';
  if (attachment.status === 'missing') return 'missing';
  return null;
}

/** Internal-note comments stay on the ticket, but only staff/team may see them. */
export function commentsVisibleToViewer<T extends { isInternal?: boolean }>(
  comments: T[] | null | undefined,
  canSeeInternal: boolean,
): T[] {
  if (!Array.isArray(comments)) return [];
  if (canSeeInternal) return comments;
  return comments.filter((comment) => !comment?.isInternal);
}
